import React, { useContext, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Context } from "../../../utils/context";

const FIELDS = [
  { name: "old_password", label: "Old Password" },
  { name: "password", label: "New Password" },
  { name: "confirm_password", label: "Confirm Password" },
];

const ChangePasswordModal = ({ show, onHide }) => {
  const { postData } = useContext(Context);
  const [visible, setVisible] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors },
  } = useForm();

  const close = () => {
    reset();
    setVisible({});
    onHide();
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const response = await postData("/common/auth/change-password", data);
      if (response?.success) {
        toast.success("Password changed successfully.");
        close();
      } else {
        const field = /old password/i.test(response?.message)
          ? "old_password"
          : "password";
        setError(field, {
          type: "manual",
          message: response?.message || "Something went wrong.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const rules = {
    old_password: {
      required: "Old password is required",
      maxLength: {
        value: 64,
        message: "Old password must not exceed 64 characters",
      },
    },
    password: {
      required: "New password is required",
      minLength: {
        value: 8,
        message: "Password must be at least 8 characters",
      },
      maxLength: {
        value: 64,
        message: "Password must not exceed 64 characters",
      },
      validate: {
        noSpaces: (v) => !/\s/.test(v) || "Password cannot contain spaces",
        uppercase: (v) =>
          /[A-Z]/.test(v) || "Password must contain an uppercase letter",
        lowercase: (v) =>
          /[a-z]/.test(v) || "Password must contain a lowercase letter",
        number: (v) => /\d/.test(v) || "Password must contain a number",
        special: (v) =>
          /[^A-Za-z0-9\s]/.test(v) ||
          "Password must contain a special character",
        different: (v) =>
          v !== watch("old_password") ||
          "New password cannot be the same as the old password",
      },
    },
    confirm_password: {
      required: "Confirm password is required",
      validate: (v) => v === watch("password") || "Passwords do not match",
    },
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Password</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Modal.Body>
          {FIELDS.map(({ name, label }) => (
            <Form.Group className="mb-3" key={name}>
              <Form.Label>{label}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={visible[name] ? "text" : "password"}
                  placeholder={label}
                  autoComplete={
                    name === "old_password"
                      ? "current-password"
                      : "new-password"
                  }
                  className={classNames({ "is-invalid": errors[name] })}
                  {...register(name, rules[name])}
                />
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setVisible((v) => ({ ...v, [name]: !v[name] }))
                  }
                >
                  <FontAwesomeIcon icon={visible[name] ? faEyeSlash : faEye} />
                </InputGroup.Text>
              </InputGroup>
              {errors[name] && (
                <span className="text-danger">{errors[name].message}</span>
              )}
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving..." : "Change Password"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
